<script lang="ts" setup>
import { ref } from "vue"
import { ButtonSize, TuIconButton, TuPopover, usePopover } from "@tinyurldev/tu-ui-vue"

import useTranslation from "@Composables/useTranslation"

//
// Props
interface Props {
  disabled?: boolean
}

withDefaults(defineProps<Props>(), { disabled: false });

//
// Setup
const { __ } = useTranslation()

//
// Emit
const emit = defineEmits<{
  (e: "resend"): void
  (e: "cancel"): void
}>()

//
// Data
const isPopoverVisible = ref(false)
const { referenceRef, popoverRef } = usePopover(isPopoverVisible, { placement: "bottom-end" })

//
// Methods
function onToggle(): void {
  isPopoverVisible.value = !isPopoverVisible.value
}

function onResendClick(): void {
  isPopoverVisible.value = false
  emit("resend")
}

function onCancelClick(): void {
  isPopoverVisible.value = false
  emit("cancel")
}
</script>

<template>
  <div class="org-user-pending-action-dropdown">
    <TuIconButton
      ref="referenceRef"
      class="_actions"
      :aria-label="__('organization_users_settings.invitation_actions')"
      icon-class="fa-solid fa-gear"
      is-outlined
      :size="ButtonSize.MEDIUM"
      :is-disabled="disabled"
      @click="onToggle"
    />

    <TuPopover
      v-show="isPopoverVisible"
      ref="popoverRef"
    >
      <div class="org-user-pending-action-dropdown__menu _actions_menu">
        <button
          class="org-user-pending-action-dropdown__menu-item _resend"
          type="button"
          @click="onResendClick"
        >
          {{ __('organization_users_settings.resend_invite') }}
        </button>
        <button
          class="org-user-pending-action-dropdown__menu-item _cancel"
          type="button"
          @click="onCancelClick"
        >
          {{ __('organization_users_settings.cancel_invite') }}
        </button>
      </div>
    </TuPopover>
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;
@use "@tinyurldev/tu-ui-vue/scss/settings/border.scss" as border;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;

.org-user-pending-action-dropdown {
  &__menu {
    display: flex;
    flex-direction: column;
    padding: spacing.$xs-5 0;

    background-color: color.$background-secondary;
    border-radius: border.$medium-radius;
  }

  &__menu-item {
    width: 100%;
    padding: spacing.$xxxxs spacing.$xxxs;

    font-size: typography.$xs-font-size;
    font-weight: typography.$font-weight-medium;
    line-height: typography.$xs-line-height;
    text-align: left;
    color: color.$text;

    background: none;
    border: none;
    cursor: pointer;

    &:hover {
      background-color: color.$background-primary;
    }
  }
}
</style>
